import { useLazyQuery } from '@apollo/client'
import { ReactElement, useState } from 'react'

import { SelectTags } from 'components/v2/SelectTags'
import { GTRouteServiceSuggestion, GTRouteServiceSuggestionsResponse } from 'gql/types.generated'
import { ISelectTagsProps } from 'components/v2/SelectTags/types'
import { useDebouncedQuery } from 'components/v2/Autocomplete'
import { GET_ROUTE_SERVICES_AUTOCOMPLETE } from 'features/Services/queries-legacy'
import { isEmpty, uniqBy } from 'lodash'
import { getEntitiesWithSameKey } from 'features/Services/components/MultiRouteServiceAutocomplete/utils'
import { MAX_INT_FOR_PAGINATION_SIZE } from 'config/constants'

const ALL_ROUTE_SERVICES_OPTION_ID = 'all'
const ALL_ROUTE_SERVICES_OPTION: GTRouteServiceSuggestion = {
  id: ALL_ROUTE_SERVICES_OPTION_ID,
  code: 'All Route Services'
}

interface IMultiRouteServiceAutocompleteProps extends Omit<ISelectTagsProps, 'onChange' | 'items' | 'getTagLabel'> {
  onChange: (routeServices: GTRouteServiceSuggestion[]) => void
  businessClientIds?: string[]
  routeIds?: string[]
  uniqueCode?: boolean
  endAt?: {
    start?: Date
    end?: Date
  }
  isShowingAllRouteServicesOption?: boolean
  verticals?: string[]
}

export const MultiRouteServiceAutocomplete = ({
  onChange,
  label,
  placeholder,
  values,
  routeIds = [],
  uniqueCode = false,
  endAt,
  isShowingAllRouteServicesOption = false,
  verticals,
  ...props
}: IMultiRouteServiceAutocompleteProps): ReactElement => {
  const [isLoadingAllRouteServices, setIsLoadingAllRouteServices] = useState(false)
  const [pattern, setPattern] = useState('')
  const [getRouteServices, { data, loading, error }] = useLazyQuery<{
    routeServiceSuggestions: GTRouteServiceSuggestionsResponse
  }>(GET_ROUTE_SERVICES_AUTOCOMPLETE)
  const { isDebouncing, shouldShowItems } = useDebouncedQuery({
    // @ts-expect-error ts(2322) FIXME
    query: getRouteServices,
    pattern,
    extraInputs: {
      routeIDs: routeIds,
      includeWithNoEndDate: true,
      endAt: endAt
        ? {
            start: endAt.start ? endAt.start.toISOString() : undefined,
            end: endAt.end ? endAt.end.toISOString() : undefined
          }
        : undefined,
      verticals: !isEmpty(verticals) ? verticals : undefined
    }
  })
  const getAllRouteServices = async (): Promise<GTRouteServiceSuggestion[]> => {
    try {
      setIsLoadingAllRouteServices(true)
      const { data: routeServicesData } = await getRouteServices({
        variables: {
          input: {
            routeIDs: routeIds
          },
          pagination: {
            page: 1,
            size: MAX_INT_FOR_PAGINATION_SIZE
          }
        }
      })
      setIsLoadingAllRouteServices(false)
      return routeServicesData?.routeServiceSuggestions?.items || []
    } catch {
      setIsLoadingAllRouteServices(false)
      return []
    }
  }

  const handleChange = async (routeServices: GTRouteServiceSuggestion[], pattern = ''): Promise<void> => {
    setPattern(pattern)

    let selectedRouteServices = routeServices
    const isSelectingAllRouteServiceOption = routeServices.some(({ id }) => id === ALL_ROUTE_SERVICES_OPTION_ID)
    if (isSelectingAllRouteServiceOption) {
      selectedRouteServices = await getAllRouteServices()
    }

    if (uniqueCode) {
      const newRouteServices = getEntitiesWithSameKey(
        selectedRouteServices,
        data?.routeServiceSuggestions?.items || [],
        'code'
      )
      onChange(newRouteServices)
    } else {
      onChange(selectedRouteServices)
    }
  }

  const selectedRouteServiceIds = values.map(({ id }) => id)
  const filteredItems =
    data?.routeServiceSuggestions?.items.filter((routeService) => {
      return !selectedRouteServiceIds.includes(routeService.id)
    }) || []
  let shownItems = uniqueCode ? uniqBy(filteredItems, 'code') : filteredItems
  shownItems =
    isEmpty(pattern) && isShowingAllRouteServicesOption ? [ALL_ROUTE_SERVICES_OPTION, ...shownItems] : shownItems

  const shownValues = uniqueCode ? uniqBy(values, 'code') : values

  return (
    <SelectTags
      placeholder={placeholder}
      values={shownValues}
      label={label}
      onChange={handleChange}
      items={shownItems}
      getTagLabel={(routeService: GTRouteServiceSuggestion): string => routeService.code}
      loading={loading || isDebouncing}
      error={error}
      shouldShowItems={shouldShowItems}
      disabled={props.disabled || isLoadingAllRouteServices}
      {...props}
    />
  )
}
